import axios, { AxiosResponse } from "axios";
import create from "zustand";
import { PagedResult } from "marvin-auth-kit";
import * as Yup from "yup";

import { useSearchParams } from "react-router-dom";
import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from "react-query";
import { useTranslation } from "react-i18next";

import {
  Attribute,
  Indicator,
  LayerSearchFormValues,
  LayersQuery as IndicatorQuery,
  PartialBy,
  UpdateIndicator,
  IndicatorBase,
  LayerProperties,
} from "types";
import { IndicatorLayersResponse, IndicatorBaseResponse } from "types/backend";
import { useWFSGetFeature } from "./spatialHooks";

import {
  deserializeIndicator,
  deserializeIndicatorBase,
  serializeIndicator,
} from "../utils/IndicatorUtils";
import { useSelectedLayer } from "./selectionHooks";

export const useIndicators = (
  config: string,
  query: IndicatorQuery = { limit: 10, offset: 0, withLayers: false },
  layerProperties: LayerProperties[]
) => {
  const fetch = async () => {
    const params = new URLSearchParams();
    if (query) {
      if (query.searchable !== undefined) {
        params.set("searchable", query.searchable ? "true" : "false");
      }
      if (query.tags && query.tags.length) {
        query.tags.forEach((tag) => params.append("tags", tag));
      }
      if (query.indicatorId && query.indicatorId.length) {
        query.indicatorId.forEach((indicatorId) =>
          params.append("indicatorId", indicatorId.toString())
        );
      }
      if (query.search) {
        params.set("search", query.search);
      }
      params.set("limit", (query.limit || 10).toString());
      params.set(
        "offset",
        ((query.offset || 0) * (query.limit || 10)).toString()
      );
      if (query.withLayers === false) params.set("withLayers", "false");
    }
    const response = await axios.get<PagedResult<IndicatorBaseResponse>>(
      `/api/${config}/indicators`,
      {
        params,
      }
    );
    if (response.status === 204) {
      return { limit: 0, totalCount: 0, offset: 0, data: [] };
    }
    return {
      ...response.data,
      data: response.data.data.map((ind) =>
        deserializeIndicatorBase(ind, layerProperties)
      ),
    };
  };
  return useQuery(["indicators", config, query], fetch);
};

export const useIndicator = (
  config: string,
  id: number | string | "",
  layerProperties: LayerProperties[],
  options?: Pick<
    UseQueryOptions,
    "suspense" | "staleTime" | "cacheTime" | "refetchOnWindowFocus"
  >
) => {
  const fetch = async () => {
    const response = await axios.get<IndicatorLayersResponse>(
      `/api/${config}/indicators/${id}`
    );

    return deserializeIndicator(response.data, layerProperties);
  };
  return useQuery(["indicator", config, id.toString()], fetch, {
    enabled: id !== "",
    cacheTime: 60000,
    ...options,
  });
};

export const useUpdateIndicator = (
  config: string,
  id: string,
  layerProperties: LayerProperties[]
) => {
  const queryClient = useQueryClient();
  const update = async (indicator: UpdateIndicator) => {
    const response = await axios.put<IndicatorLayersResponse>(
      `/api/${config}/indicators/${indicator.id}`,
      serializeIndicator(indicator)
    );

    return deserializeIndicator(response.data, layerProperties);
  };
  return useMutation(update, {
    onSuccess: (updatedData) => {
      queryClient.invalidateQueries([
        ["indicators", config],
        ["indicator", id, config],
      ]);
      queryClient.setQueryData(["indicator", id, config], updatedData);
    },
  });
};

export const useDeleteIndicator = (config: string, id: string) => {
  const queryClient = useQueryClient();
  const remove = async (indicator: Indicator | IndicatorBase) => {
    const response = await axios.delete<string>(
      `/api/${config}/indicators/${indicator.id}`
    );

    return response.data;
  };
  return useMutation(remove, {
    onSuccess: () => queryClient.invalidateQueries(["indicators", config]),
  });
};

export const useEmptyIndicator = () => {
  async function fetch() {
    const EMPTY_LAYER: PartialBy<Indicator, "id"> = {
      image: "",
      title: "",
      order: 0,
      opacity: 70,
      isDefaultActive: false,
      isDefaultSelected: false,
      groupingKey: "",
      tags: [],
      attribution: "",
      information: [],
      layers: [],
    };

    return Promise.resolve(EMPTY_LAYER);
  }

  return useQuery(["new-indicator"], fetch, {
    cacheTime: Infinity,
    staleTime: Infinity,
  });
};

export const useIndicatorValidation = () => {
  const { t } = useTranslation();

  return Yup.object().shape({
    title: Yup.string().required(
      t("validation.requiredField", { field: t("label.name") })
    ),
    // layers: Yup.array().of(
    //   Yup.object({
    //     wms: Yup.object({
    //       url: Yup.string()
    //         .url()
    //         .required(t("validation.requiredField", { field: t("label.url") })),
    //     }),
    //   })
    // ),
  });
};

export const useCreateIndicator = (
  config: string,
  layerProperties: LayerProperties[]
) => {
  const queryClient = useQueryClient();
  const create = async (indicator: Omit<Indicator, "id">) => {
    const response = await axios.post<
      Omit<IndicatorLayersResponse, "id">,
      AxiosResponse<IndicatorLayersResponse>
    >(`/api/${config}/indicators`, serializeIndicator(indicator));

    return deserializeIndicator(response.data, layerProperties);
  };
  return useMutation(["indicator", "create", config], create, {
    onSuccess: (createdIndicator) =>
      queryClient.invalidateQueries([
        ["indicators", config],
        ["indicator", config, createdIndicator.id],
      ]),
  });
};

export const useAttrtibute = (
  config: string,
  indicatorId: number | "",
  attributeKey: string,
  layerProperties: LayerProperties[]
) => {
  const { data: indicator } = useIndicator(
    config,
    indicatorId,
    layerProperties
  );
  const fetch = async () => {
    const attribute = (
      indicator?.layers.flatMap((layer) => layer.attributes) || []
    ).find((attribute: Attribute) => attribute.key === attributeKey);

    return Promise.resolve(attribute);
  };
  return useQuery(
    ["indicators", "attribute", attributeKey, indicatorId, config],
    fetch,
    {
      enabled: indicatorId !== "" && indicator !== undefined,
    }
  );
};

export const useSearchResult = (
  searchQuery: LayerSearchFormValues,
  config: string,
  layerProperties: LayerProperties[]
) => {
  const { query, attributes } = searchQuery;
  const { data: indicator } = useIndicator(
    config,
    searchQuery.layer,
    layerProperties
  );
  const { data: layer } = useSelectedLayer(config, indicator, layerProperties);
  const objectType = attributes.reduce(
    (merged: { [key: string]: string }, current: string) => {
      return { ...merged, [current]: "" };
    },
    {}
  );
  //TODO: quick and DIRTY HACK!! needs to be persisted to the backend!
  const layerAttributes = layer?.attributes.filter(
    (attr) => attributes.indexOf(attr.key) > -1
  );
  const geomProperty =
    layerAttributes
      ?.filter((layerAttr) => layerAttr.layers.startsWith("geom:"))
      .map((layerAttr) => layerAttr.layers.replace(/^geom:/, ""))
      .shift() || "geom";

  return useWFSGetFeature<typeof objectType>(
    layer?.wms?.url || "",
    {
      typeNames: layer?.wms.layers,
      propertyName: [...attributes, geomProperty].join(","),
      count: 20,
      cql_filter: attributes
        .map(
          (attribute) =>
            `strToLowerCase(${attribute}) like '%${query.toLowerCase()}%'`
        )
        .join(" or "),
    },
    {
      enabled: indicator !== undefined && layer !== undefined,
    }
  );
};

const useBackgroundIndicatorSearchParam = (): [
  string | null,
  (value: number) => void
] => {
  const [params, setParams] = useSearchParams();

  function setParamsWrapper(id: number) {
    const updated = new URLSearchParams(params);
    updated.set("bg", id.toString());
    return setParams(updated);
  }

  return [params.get("bg"), setParamsWrapper];
};

export const useBackgroundIndicator = (
  backgroundIndicators: Indicator[]
): [Indicator | undefined, (indicator: Indicator) => void] => {
  const [backgroundIndicatorParam, setBackgroundIndicatorParam] =
    useBackgroundIndicatorSearchParam();
  const [firstDefault] = backgroundIndicators.reduce(
    (defaultIndicators: Indicator[], indicator: Indicator, index: number) => {
      if (index === 0) {
        return [...defaultIndicators, indicator];
      } else if (indicator.isDefaultActive) {
        return [indicator, ...defaultIndicators];
      }

      return [...defaultIndicators];
    },
    []
  );
  const [defaultIndicator] = [
    ...backgroundIndicators.filter(
      (indicator) => indicator.id.toString() === backgroundIndicatorParam
    ),
    firstDefault,
  ];

  const [backgroundIndicator, setBackgroundIndicator] =
    useBackgroundIndicatorStore((state) => [
      state.indicator,
      state.setIndicator,
    ]);

  function setBackgroundLayerWrapper(indicator: Indicator) {
    setBackgroundIndicatorParam(indicator.id);
    return setBackgroundIndicator(indicator);
  }
  return [backgroundIndicator || defaultIndicator, setBackgroundLayerWrapper];
};

type BackgroundIndicatorStoreState = {
  indicator: Indicator;
  setIndicator: (layer: Indicator) => void;
};

const useBackgroundIndicatorStore = create<BackgroundIndicatorStoreState>(
  (set) => ({
    indicator: null!,
    setIndicator: (indicator: Indicator) => set({ indicator }),
  })
);

export const useTags = (config: string) => {
  async function fetch() {
    const response = await axios.get<string[]>(`/api/${config}/tags`);
    return response.data;
  }
  return useQuery(["tags", config], fetch);
};
